import React, { useState } from "react";
import Marquee from "react-fast-marquee";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, Gamepad2, Brain, HelpCircle, Users, Trophy, Crown, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { GameCard } from "@/components/GameCard";
import { AdBanner } from "@/components/AdBanner";
import { DailyChallenge } from "@/components/DailyChallenge";
import Seo from "@/components/Seo";
import { GAMES, QUIZZES } from "@/data/games";
import { useAuth } from "@/context/AuthContext";

const HERO_BG = "https://images.unsplash.com/photo-1511512578047-dfb367046420?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const CATEGORY_TILES = [
  { to: "/arcade", label: "Arcade", desc: "Snake, 2048, Memory & more", icon: Gamepad2, color: "#00F0FF",
    img: "https://images.unsplash.com/photo-1562229125-6d6075419a22?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
  { to: "/brain-teasers", label: "Brain Teasers", desc: "IQ tests & memory challenges", icon: Brain, color: "#39FF14",
    img: "https://images.unsplash.com/photo-1577401239170-897942555fb3?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
  { to: "/quizzes", label: "Quizzes", desc: "Which gamer are you?", icon: HelpCircle, color: "#FF003C",
    img: "https://images.unsplash.com/photo-1511988617509-a57c8a288659?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
];

export default function Home() {
  const navigate = useNavigate();
  const { user, setShowAuth } = useAuth();
  const featured = [...GAMES.slice(0, 3), ...QUIZZES.slice(0, 1), ...GAMES.slice(3, 6), ...QUIZZES.slice(1, 2)];

  return (
    <div data-testid="home-page">
      <Seo title="Free Online Arcade Games, Brain Teasers & Quizzes" description="Play cool arcade games, brain teasers, IQ tests and viral BuzzFeed-style quizzes. Compete on leaderboards, challenge a friend, and go ad-free with Premium." />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="Neon arcade" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/70" />
          <div className="absolute inset-0 grid-bg opacity-40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-24 md:py-32">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-3 py-1 mb-5 text-xs font-bold uppercase tracking-widest bg-neon-cyan/10 border border-neon-cyan text-neon-cyan rounded-full">
              <Sparkles className="w-3 h-3" /> 100% free to start playing
            </span>
            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl uppercase tracking-tighter leading-[0.95]">
              Play. Compete.<br /><span className="text-neon-cyan text-glow">Go Mania.</span>
            </h1>
            <p className="mt-6 text-lg text-zinc-300 max-w-xl">
              The arcade that never closes. Dive into addictive games, brain-bending teasers, and viral quizzes — then challenge a friend head to head.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button onClick={() => navigate("/arcade")} data-testid="hero-play-btn"
                className="bg-neon-cyan text-black rounded-none font-bold uppercase tracking-widest h-14 px-8 text-base hover:bg-white neo-shadow-pink">
                <Play className="w-5 h-5 mr-2 fill-black" /> Start Playing
              </Button>
              <Button onClick={() => navigate("/play/reaction-duel")} variant="outline"
                className="border-zinc-600 bg-transparent text-white rounded-none font-bold uppercase tracking-widest h-14 px-8 text-base hover:border-neon-pink hover:text-neon-pink">
                <Users className="w-5 h-5 mr-2" /> Play a Friend
              </Button>
            </div>
          </motion.div>
        </div>

        {/* Marquee */}
        <div className="relative border-y-2 border-neon-yellow bg-black py-3">
          <Marquee speed={60} gradient={false}>
            {Array.from({ length: 4 }).map((_, i) => (
              <span key={i} className="font-display font-black text-2xl uppercase tracking-tight text-neon-yellow mx-8">
                Level Up Your Brain <span className="text-zinc-700 mx-2">•</span> Play Against Friends <span className="text-zinc-700 mx-2">•</span> No Ads With Premium <span className="text-zinc-700 mx-2">•</span>
              </span>
            ))}
          </Marquee>
        </div>
      </section>

      <div className="py-10">
        <DailyChallenge />
      </div>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <h2 className="font-display font-black text-3xl md:text-4xl uppercase tracking-tight mb-8">Pick Your Poison</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {CATEGORY_TILES.map((c, i) => (
            <motion.div key={c.to} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}>
              <Link to={c.to} data-testid={`category-${c.label.toLowerCase().replace(/\s/g, "-")}`}
                className="group relative block aspect-[4/3] rounded-xl overflow-hidden border-2 border-zinc-800 hover:-translate-y-1 transition-all duration-300"
                style={{ boxShadow: "none" }}>
                <img src={c.img} alt={c.label} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                <div className="absolute bottom-0 left-0 p-6">
                  <c.icon className="w-8 h-8 mb-2" style={{ color: c.color }} />
                  <h3 className="font-display font-black text-2xl uppercase tracking-tight">{c.label}</h3>
                  <p className="text-sm text-zinc-400">{c.desc}</p>
                </div>
                <div className="absolute top-0 left-0 w-full h-1 group-hover:h-1.5 transition-all" style={{ background: c.color }} />
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <AdBanner slot="home" className="mb-8" />
      </div>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-end justify-between mb-8">
          <h2 className="font-display font-black text-3xl md:text-4xl uppercase tracking-tight">Featured Games</h2>
          <Link to="/games" className="text-neon-cyan font-semibold uppercase text-sm tracking-widest hover:underline">Browse all →</Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {featured.map((g, i) => <GameCard key={g.id} item={g} index={i} />)}
        </div>
      </section>

      {/* PREMIUM CTA */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="relative rounded-2xl border-2 border-neon-pink bg-zinc-950 overflow-hidden p-8 md:p-12 text-center">
          <div className="absolute inset-0 grid-bg opacity-20" />
          <div className="relative">
            <Crown className="w-12 h-12 text-neon-yellow mx-auto mb-4" />
            <h2 className="font-display font-black text-3xl md:text-5xl uppercase tracking-tight">Ditch the Ads</h2>
            <p className="text-zinc-300 mt-3 max-w-xl mx-auto">Go Premium for an ad-free experience, unlimited plays, and the cleanest way to dominate the leaderboard.</p>
            <Button onClick={() => navigate("/subscribe")} data-testid="home-premium-btn"
              className="mt-6 bg-neon-pink text-white rounded-none font-bold uppercase tracking-widest h-14 px-10 text-base hover:bg-white hover:text-black">
              Get Premium — $4.99/mo
            </Button>
          </div>
        </div>
      </section>

      {!user && (
        <section className="max-w-3xl mx-auto px-4 md:px-8 pb-16 text-center">
          <Trophy className="w-10 h-10 text-neon-cyan mx-auto mb-3" />
          <h3 className="font-display font-black text-2xl uppercase tracking-tight">Save Your High Scores</h3>
          <p className="text-zinc-400 mt-2 mb-5">Create a free account to play unlimited games and climb the global leaderboard.</p>
          <Button onClick={() => setShowAuth(true)} data-testid="home-signup-btn"
            className="bg-neon-cyan text-black rounded-none font-bold uppercase tracking-widest h-12 px-8">
            Create Free Account
          </Button>
        </section>
      )}
    </div>
  );
}
